/**
 * Call classification + canonical-aggregate helpers.
 *
 * /sales reads call data from the canonical Call Tracking sheet via the
 * shell route /api/sales/calls/data. There is no legacy fallback — D-SCSM-6
 * "feature-flag-gated cutover" was dropped after the env-var bake-in fight
 * proved the flag was infrastructure tax for a rollback path we're not going
 * to use. If we ever need to revert, git revert the canonical-migration commits.
 *
 * Connected = Duration_Seconds 120-299 (aligned to /companywide; D-SCSM-4).
 *
 * See cto-sessions-rpos/DECISION_LOG.md D-SCSM-1..7.
 */

export function isConnected(call) {
  if (!call) return false;
  const sec = parseFloat(call.Duration_Seconds);
  return sec >= 120 && sec <= 299;
}

/**
 * Fetch canonical raw call rows from the Phase 1 shell route.
 * Returns array of { Date, Rep, Account, Subject, Duration_Minutes, Duration_Seconds, Time_of_Call, Task_ID }.
 */
export async function fetchCanonicalCalls(customStart, customEnd, team) {
  const params = new URLSearchParams({
    dateRange: 'custom',
    customStart: customStart,
    customEnd: customEnd,
    team: team,
    _t: Date.now()
  });
  const url = `https://revenuepipelineos.com/api/sales/calls/data?${params}`;
  const resp = await fetch(url, { credentials: 'include' });
  if (!resp.ok) throw new Error('canonical calls fetch failed: ' + resp.status);
  const json = await resp.json();
  return json?.tabs?.Dials_TalkTime?.data || [];
}

/**
 * Group canonical raw call rows by (Rep, dateStr 'yyyy-MM-dd') and compute the
 * five calls-related fields that AGG_Daily_Activity carries.
 *
 * Returned shape — keyed by `${Rep}|${Date}`:
 *   { TotalCalls, ConnectedCalls, TalkMinutes, UniqueAccounts, UniqueConnectedAccounts }
 *
 * ConnectedCalls uses D-SCSM-4 threshold (Duration_Seconds 120-299) via isConnected().
 */
export function buildDailyCallsIndex(rawCalls) {
  const idx = {};
  for (let i = 0; i < rawCalls.length; i++) {
    const c = rawCalls[i];
    if (!c || !c.Rep || !c.Date) continue;
    const dateStr = String(c.Date).split('T')[0];
    const key = c.Rep + '|' + dateStr;
    if (!idx[key]) {
      idx[key] = {
        TotalCalls: 0,
        ConnectedCalls: 0,
        TalkMinutes: 0,
        _accountSet: {},
        _connectedAccountSet: {}
      };
    }
    const b = idx[key];
    b.TotalCalls += 1;
    b.TalkMinutes += parseFloat(c.Duration_Minutes) || 0;
    const connected = isConnected(c);
    if (connected) b.ConnectedCalls += 1;
    const acct = (c.Account || '').toString().trim();
    if (acct) {
      b._accountSet[acct] = true;
      if (connected) b._connectedAccountSet[acct] = true;
    }
  }
  const out = {};
  Object.keys(idx).forEach(function(key) {
    const b = idx[key];
    out[key] = {
      TotalCalls: b.TotalCalls,
      ConnectedCalls: b.ConnectedCalls,
      TalkMinutes: b.TalkMinutes,
      UniqueAccounts: Object.keys(b._accountSet).length,
      UniqueConnectedAccounts: Object.keys(b._connectedAccountSet).length
    };
  });
  return out;
}

/**
 * Splice canonical-computed calls fields into a legacy AGG_Daily_Activity row set.
 *
 * Phase 2.3 (D-SCSM-11): canonical is the sole source of truth for calls fields.
 * Phase 2 only overrode matching legacy rows (left legacy-only dates as-is).
 * Phase 2.2 added canonical-only buckets via append. Phase 2.3 closes the
 * inverse leak: legacy-only (Rep, Date) buckets get their calls fields ZEROED
 * (canonical's silence = "no calls"). SMS fields stay legacy per D-SCSM-2.
 *
 * Output is the UNION of (Rep, Date) keys across canonical and legacy:
 *   • Calls fields  → canonical values; 0 if canonical lacks key
 *   • SMS fields    → legacy values; 0 if legacy lacks key
 *   • Date / Month / Rep → from legacy if present, else synthesized from key
 *
 * Fail-safe: if canonical fetch returned empty (network error, auth issue), pass
 * legacy rows through unchanged. Graceful degradation — /sales falls back to
 * its pre-canonical behavior rather than zeroing every call.
 *
 * Returns a new array; does not mutate input.
 */
var MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
function dateStrFromValue(d) {
  if (!d) return '';
  return (d instanceof Date) ? d.toISOString().split('T')[0] : String(d).split('T')[0];
}
function monthNameFromDateStr(dateStr) {
  const idx = parseInt(dateStr.substring(5, 7), 10) - 1;
  return (idx >= 0 && idx < 12) ? MONTH_NAMES[idx] : '';
}

export function spliceDailyCanonicalIntoLegacy(legacyDailyRows, canonicalIndex) {
  const canonicalKeys = Object.keys(canonicalIndex);

  // Fail-safe: empty canonical → legacy passthrough. Keeps /sales working if
  // the canonical fetch failed for any reason.
  if (canonicalKeys.length === 0) {
    return legacyDailyRows;
  }

  // Index legacy rows by (Rep, Date) key so we can join with canonical.
  const legacyByKey = {};
  for (let i = 0; i < legacyDailyRows.length; i++) {
    const r = legacyDailyRows[i];
    if (!r || !r.Rep || !r.Date) continue;
    legacyByKey[r.Rep + '|' + dateStrFromValue(r.Date)] = r;
  }

  // Union of keys from both sides.
  const allKeys = {};
  for (let i = 0; i < canonicalKeys.length; i++) allKeys[canonicalKeys[i]] = true;
  const legacyKeysList = Object.keys(legacyByKey);
  for (let i = 0; i < legacyKeysList.length; i++) allKeys[legacyKeysList[i]] = true;
  const allKeysList = Object.keys(allKeys);

  const out = [];
  for (let i = 0; i < allKeysList.length; i++) {
    const key = allKeysList[i];
    const sep = key.indexOf('|');
    const rep = key.substring(0, sep);
    const dateStr = key.substring(sep + 1);
    const c = canonicalIndex[key];
    const l = legacyByKey[key];
    out.push({
      Date: l ? l.Date : dateStr,
      Month: l ? l.Month : monthNameFromDateStr(dateStr),
      Rep: rep,
      TotalCalls: c ? c.TotalCalls : 0,
      ConnectedCalls: c ? c.ConnectedCalls : 0,
      TalkMinutes: c ? c.TalkMinutes : 0,
      UniqueAccounts: c ? c.UniqueAccounts : 0,
      UniqueConnectedAccounts: c ? c.UniqueConnectedAccounts : 0,
      TotalSMS: l ? (parseInt(l.TotalSMS) || 0) : 0,
      SMSOut: l ? (parseInt(l.SMSOut) || 0) : 0,
      SMSIn: l ? (parseInt(l.SMSIn) || 0) : 0
    });
  }
  return out;
}

/**
 * Compute the inclusive 13-month rolling window in YYYY-MM-DD strings.
 * Returns { start, end } — start is 13 months ago at day 1, end is today.
 */
export function thirteenMonthWindow() {
  const now = new Date();
  const end = now.toISOString().split('T')[0];
  const startDate = new Date(now.getFullYear(), now.getMonth() - 12, 1);
  const start = startDate.toISOString().split('T')[0];
  return { start: start, end: end };
}
